import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useState,
  type ReactNode,
} from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'comforttool_cookie_choice';

export type CookieChoice = 'necessary' | 'analytics' | null;

type Ctx = {
  choice: CookieChoice;
  setChoice: (c: Exclude<CookieChoice, null>) => void;
  analyticsAllowed: boolean;
};

const CookieConsentContext = createContext<Ctx | null>(null);

export function useCookieConsent(): Ctx {
  const x = useContext(CookieConsentContext);
  if (!x) throw new Error('useCookieConsent binnen CookieConsentProvider');
  return x;
}

function readInitial(): CookieChoice {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === 'necessary' || v === 'analytics') return v;
  } catch {
    /* ignore */
  }
  return null;
}

export function CookieConsentProvider({ children }: { children: ReactNode }) {
  const [choice, setChoiceState] = useState<CookieChoice>(() =>
    typeof window === 'undefined' ? null : readInitial()
  );

  const setChoice = useCallback((c: Exclude<CookieChoice, null>) => {
    try {
      localStorage.setItem(STORAGE_KEY, c);
    } catch {
      /* ignore */
    }
    setChoiceState(c);
  }, []);

  const value = useMemo(
    () => ({
      choice,
      setChoice,
      analyticsAllowed: choice === 'analytics',
    }),
    [choice, setChoice]
  );

  return (
    <CookieConsentContext.Provider value={value}>{children}</CookieConsentContext.Provider>
  );
}

/** Banner tot gebruiker kiest; analytics = optionele meetpixels later (nu geen third-party load). */
export function CookieBanner() {
  const { choice, setChoice } = useCookieConsent();
  if (choice !== null) return null;

  return (
    <div
      className="fixed bottom-0 left-0 right-0 z-[100] border-t border-ct-border bg-ct-surface/95 px-4 py-4 shadow-[0_-8px_32px_rgba(0,0,0,0.35)] backdrop-blur-md"
      role="dialog"
      aria-labelledby="cookie-banner-title"
      aria-describedby="cookie-banner-desc"
    >
      <div className="mx-auto flex max-w-4xl flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div className="text-sm text-ct-muted" id="cookie-banner-desc">
          <p id="cookie-banner-title" className="font-semibold text-ct-text">
            Cookies en privacy
          </p>
          <p className="mt-1 leading-relaxed">
            We gebruiken functionele cookies die nodig zijn voor de app. Optioneel kun je anonieme statistiek
            toestaan (nu nog geen externe trackers geladen).{' '}
            <Link to="/cookies" className="text-ct-accent underline-offset-2 hover:underline">
              Meer over cookies
            </Link>
          </p>
        </div>
        <div className="flex flex-shrink-0 flex-wrap gap-2">
          <button
            type="button"
            onClick={() => setChoice('necessary')}
            className="rounded-xl border border-ct-border px-4 py-2 text-sm font-medium text-ct-text hover:bg-ct-elevated"
          >
            Alleen noodzakelijk
          </button>
          <button
            type="button"
            onClick={() => setChoice('analytics')}
            className="rounded-xl bg-ct-accent px-4 py-2 text-sm font-bold text-white hover:bg-blue-500"
          >
            Akkoord (+ statistiek)
          </button>
        </div>
      </div>
    </div>
  );
}
